"use client";

import { useForm, useWatch, type FieldErrors } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type infer as ZodInfer } from "zod";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

// UI Components
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ShoppingCart,
  Truck,
  Calendar,
  FileText,
  DollarSign,
  Tag,
  User,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Custom Components
import { ComboSearch } from "@/components/ComboSearch";
import OrderMarginAnalysis from "@/components/OrderMarginAnalysis";
import UnifiedProductSelector from "@/components/UnifiedProductSelector";
import { DatePickerComponent } from "@/components/DatePickerComponent";
import { DateTimePicker } from "@/components/DatetimePickerComponent";
import { LoadingSpinner } from "@/components/LoadingSpinner";

// Services
import { CustomerService } from "@/services/CustomerService";
import { ProductsService } from "@/services/ProductsService";
import { ProductAdditionalsService } from "@/services/ProductAdditionalsService";
import { OrderService } from "@/services/OrderService";

// Interfaces
import {
  type Address,
  type CustomersOutput,
  orderDeliveryTypeTransalation,
  OrderDeliveryType,
  OrderStatus,
  orderStatusTranslation,
  type ProductsOutput,
  type OrderOutput,
} from "@/interfaces";
import MultiTagSelector from "@/components/MultiTagSelector";
import CustomCombobox from "@/components/CustomCombobox";
import { schema } from "./schema/schema";
import { localDate } from "@/util/dates";
import { numberToMoney } from "@/util/moneyConversion";
import CurrencyInput from "@/components/CurrencyInput";
import AddressSelectorComponent from "@/components/AddressSelectorComponent";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// Types
type OrderFormValues = ZodInfer<typeof schema>;

type FormValidationIssue = {
  field: string;
  message: string;
};

type OrderFormTab = "info" | "products" | "delivery";

const collectValidationIssues = (
  errors: unknown,
  parentPath = "",
  visited = new WeakSet<object>()
): FormValidationIssue[] => {
  if (!errors || typeof errors !== "object") {
    return [];
  }

  if (visited.has(errors)) {
    return [];
  }

  visited.add(errors);

  const issues: FormValidationIssue[] = [];
  const entries: [string, unknown][] = Array.isArray(errors)
    ? errors.map((value, index) => [String(index), value] as const)
    : (Object.entries(errors) as [string, unknown][])
        .filter(([key]) => !["ref", "type", "types"].includes(key));

  for (const [key, value] of entries) {
    if (!value) {
      continue;
    }

    const path = parentPath ? `${parentPath}.${key}` : key;
    const valueAsObject = value as { message?: unknown };

    if (typeof valueAsObject.message === "string") {
      issues.push({ field: path, message: valueAsObject.message });
    }

    if (typeof value === "object") {
      issues.push(...collectValidationIssues(value, path, visited));
    }
  }

  return issues;
};

const getTabFromField = (fieldPath: string): OrderFormTab => {
  if (fieldPath.startsWith("products")) {
    return "products";
  }

  if (fieldPath.startsWith("delivery")) {
    return "delivery";
  }

  return "info";
};

export default function OrderForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;

  const customerService = useMemo(() => new CustomerService(), []);
  const productService = useMemo(() => new ProductsService(), []);
  const orderService = useMemo(() => new OrderService(), []);

  const [customers, setCustomers] = useState<CustomersOutput[]>([]);
  const [products, setProducts] = useState<ProductsOutput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");
  const [orderDetails, setOrderDetails] = useState<OrderOutput | null>(null);
  const [selectedCustomerDetails, setSelectedCustomerDetails] =
    useState<CustomersOutput | null>(null);

  const form = useForm<OrderFormValues, undefined, OrderFormValues>({
    resolver: zodResolver(schema) as Resolver<
      OrderFormValues,
      undefined,
      OrderFormValues
    >,
    defaultValues: {
      orderDate: localDate(),
      status: OrderStatus.PENDING,
      delivery: {
        deliveryType: OrderDeliveryType.WITHDRAWAL,
      },
      preparationDate: localDate(),
      products: [],
    },
  });

  useEffect(() => {
    let isSubscribed = true;

    const hydrateProductsWithAdditionals = async (
      baseProducts: ProductsOutput[]
    ): Promise<ProductsOutput[]> => {
      const hydrated: ProductsOutput[] = [];
      const batchSize = 5;

      for (let index = 0; index < baseProducts.length; index += batchSize) {
        const slice = baseProducts.slice(index, index + batchSize);
        const enriched = await Promise.all(
          slice.map(async (product) => {
            if (!product.id) {
              return product;
            }

            try {
              const additionalsService = new ProductAdditionalsService(
                product.id
              );
              const additionals = await additionalsService.list();
              return { ...product, additional: additionals };
            } catch (error) {
              console.error(
                `Erro ao carregar adicionais do produto ${product.id}:`,
                error
              );
              return { ...product, additional: [] };
            }
          })
        );

        hydrated.push(...enriched);
      }

      return hydrated;
    };

    const fetchMissingProducts = async (
      productIds: string[]
    ): Promise<ProductsOutput[]> => {
      const uniqueIds = Array.from(new Set(productIds));
      if (uniqueIds.length === 0) {
        return [];
      }

      const results = await Promise.all(
        uniqueIds.map(async (productId) => {
          try {
            const additionalsService = new ProductAdditionalsService(productId);
            const [product, additionals] = await Promise.all([
              productService.findById(productId, { expand: ["file"] }),
              additionalsService.list(),
            ]);

            return { ...product, additional: additionals };
          } catch (error) {
            console.error(`Erro ao carregar produto ${productId}:`, error);
            return null;
          }
        })
      );

      return results.filter((product) => product !== null);
    };

    const fetchInitialData = async () => {
      try {
        setIsLoading(true);

        const [{ data: fetchedCustomers }, { data: fetchedProducts }] =
          await Promise.all([
            customerService.findAll({ pageSize: 100 }),
            productService.findAll({ pageSize: 100, expand: ["file"] }),
          ]);

        let hydratedProducts = await hydrateProductsWithAdditionals(
          fetchedProducts
        );

        let details: OrderOutput | null = null;

        if (isEditing && id) {
          details = await orderService.findById(id);

          const missingProductIds = details.products
            .map((product) => product.productId)
            .filter(
              (productId): productId is string =>
                Boolean(productId) &&
                !hydratedProducts.some((item) => item.id === productId)
            );

          if (missingProductIds.length > 0) {
            const extraProducts = await fetchMissingProducts(missingProductIds);
            hydratedProducts = [...hydratedProducts, ...extraProducts];
          }
        }

        if (!isSubscribed) {
          return;
        }

        setCustomers(fetchedCustomers);
        setProducts(hydratedProducts);

        if (details) {
          setOrderDetails(details);

          form.reset({
            ...details,
            products: details.products.map((product) => {
              const normalizedAdditionals =
                product.additionals
                  ?.map((additional) => ({
                    itemId: additional.itemId,
                    quantity: additional.quantity,
                  }))
                  .filter(
                    (additional) => additional.itemId && additional.quantity > 0
                  ) ?? [];

              return {
                productId: product.productId,
                quantity: product.quantity,
                observation: product.observation ?? undefined,
                additionals:
                  normalizedAdditionals.length > 0
                    ? normalizedAdditionals
                    : undefined,
              };
            }),
            tags: details.tags as string[],
            customerId: details.customerId,
            preparationDate: new Date(details.preparationDate),
            orderDate: new Date(details.orderDate),
            delivery: {
              ...details.delivery,
              deliveryAt: details.delivery.deliveryAt
                ? new Date(details.delivery.deliveryAt)
                : localDate(),
            },
          });
        } else {
          setOrderDetails(null);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        if (isSubscribed) {
          setIsLoading(false);
        }
      }
    };

    fetchInitialData();

    return () => {
      isSubscribed = false;
    };
  }, [id, form, customerService, orderService, productService, isEditing]);

  // Product Handling
  const watchProducts = (form.watch("products") ??
    []) as OrderFormValues["products"];
  const watchAdditional = form.watch("additional") ?? 0;
  const watchCustomerId = form.watch("customerId") ?? "";
  const deliveryValue = form.watch("delivery.deliveryValue") ?? 0;
  const discountWatch = form.watch("discount") ?? 0;

  type ProductAdditionalForMargin = {
    itemId: string;
    label: string;
    quantity: number;
    unitPrice: number;
    unitCost: number;
  };

  const findProductById = (productId: string) =>
    products.find((product) => product.id === productId);

  const getProductAdditionalDetails = (
    productRow: OrderFormValues["products"][number]
  ): ProductAdditionalForMargin[] => {
    const product = findProductById(productRow.productId);
    if (!product || !product.additional?.length || !productRow.additionals) {
      return [] as ProductAdditionalForMargin[];
    }

    const availableItems = product.additional.flatMap((group) => group.items);

    return productRow.additionals
      .map((additional) => {
        const item = availableItems.find(
          (candidate) => candidate.id === additional.itemId
        );
        if (!item) {
          return null;
        }

        return {
          itemId: additional.itemId,
          label: item.name,
          quantity: additional.quantity,
          unitPrice: item.unitPrice ?? 0,
          unitCost: item.unitCost ?? 0,
        };
      })
      .filter(Boolean) as ProductAdditionalForMargin[];
  };

  const calculateTotalValue = () => {
    const subtotal = watchProducts.reduce((sum, productRow) => {
      const product = findProductById(productRow.productId);
      const productValue =
        (product?.unitPrice ?? 0) * (productRow.quantity ?? 0);

      const additionalsValue = getProductAdditionalDetails(productRow).reduce(
        (additionalSum, additional) =>
          additionalSum + additional.unitPrice * (additional.quantity ?? 0),
        0
      );

      return sum + productValue + additionalsValue;
    }, 0);

    const total = subtotal + watchAdditional + deliveryValue - discountWatch;
    return numberToMoney(total);
  };

  const getProductPriceValue = (productId: string): number => {
    const product = findProductById(productId);
    return product?.unitPrice || 0;
  };

  const getProductCostValue = (productId: string): number => {
    const product = findProductById(productId);
    return product?.unitCost || 0;
  };

  const getProductName = (productId: string): string => {
    const product = findProductById(productId);
    return product?.name || "Produto não encontrado";
  };

  const deliveryTypeWatch = useWatch({
    name: "delivery.deliveryType",
    control: form.control,
  });

  const getCustomerAddresses = (
    customer?: (CustomersOutput & { address?: Address | Address[] }) | null
  ): Address[] => {
    if (!customer) {
      return [];
    }

    if (Array.isArray(customer.addresses) && customer.addresses.length > 0) {
      return customer.addresses;
    }

    if (Array.isArray(customer.address)) {
      return customer.address;
    }

    if (customer.address) {
      return [customer.address];
    }

    return [];
  };

  useEffect(() => {
    let isSubscribed = true;

    if (!watchCustomerId) {
      setSelectedCustomerDetails(null);
      return () => {
        isSubscribed = false;
      };
    }

    const customerFromList =
      customers.find((customer) => customer.id === watchCustomerId) ?? null;

    if (customerFromList) {
      setSelectedCustomerDetails(customerFromList);
    }

    const fetchSelectedCustomer = async () => {
      try {
        const customer = await customerService.findById(watchCustomerId);

        if (!isSubscribed) {
          return;
        }

        setSelectedCustomerDetails(customer);
      } catch (error) {
        console.error("Erro ao carregar detalhes do cliente:", error);
      }
    };

    fetchSelectedCustomer();

    return () => {
      isSubscribed = false;
    };
  }, [watchCustomerId, customers, customerService]);

  useEffect(() => {
    if (deliveryTypeWatch !== OrderDeliveryType.DELIVERY) {
      return;
    }

    const addresses = getCustomerAddresses(selectedCustomerDetails);
    const currentAddress = form.getValues("delivery.address");

    if (!currentAddress && addresses.length > 0) {
      form.setValue("delivery.address", addresses[0]);
      return;
    }

    if (currentAddress && addresses.length === 0) {
      form.setValue("delivery.address", undefined);
    }
  }, [deliveryTypeWatch, selectedCustomerDetails, form]);

  const onSubmit = async (values: OrderFormValues) => {
    try {
      setIsLoading(true);
      const data = { ...values };

      if (data.delivery.deliveryType === OrderDeliveryType.WITHDRAWAL) {
        delete data.delivery.deliveryAt;
        delete data.delivery.address;
        delete data.delivery.deliveryValue;
      } else {
        data.delivery.deliveryAt = localDate(data.delivery.deliveryAt);
        data.delivery.deliveryValue = deliveryValue;
      }

      if (isEditing && id) {
        await orderService.update(id, data);
        navigate(`/pedidos/${id}`);
      } else {
        await orderService.create(data);
        navigate("/pedidos");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalidSubmit = (errors: FieldErrors<OrderFormValues>) => {
    const issues = collectValidationIssues(errors);

    console.warn("Validacao do pedido falhou. Campos pendentes:", issues);
    if (issues.length > 0) {
      console.table(issues);

      const firstIssue = issues[0];
      const firstTab = getTabFromField(firstIssue.field);
      setActiveTab(firstTab);

      const description =
        issues.length > 1
          ? `${firstIssue.message} (+${issues.length - 1} pendencia(s))`
          : firstIssue.message;

      toast.error("Nao foi possivel salvar o pedido", {
        description,
      });
    } else {
      console.warn("Erros brutos de validacao:", errors);
      toast.error("Nao foi possivel salvar o pedido", {
        description: "Revise os campos obrigatorios e tente novamente.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  const getCustomerName = (customerId: string) => {
    const customer = customers.find((c) => c.id === customerId);
    return customer ? customer.name : "Cliente não encontrado";
  };

  return (
    <Form {...form}>
      <div className="p-2 sm:p-4 md:p-6 max-w-4xl mx-auto">
        {isEditing && orderDetails && (
          <div className="mb-4 p-3 bg-orange-50 rounded-md">
            <p className="text-sm text-gray-600">
              Editando pedido: {orderDetails.id}
              {orderDetails.customerId && (
                <span className="block sm:inline sm:ml-2">
                  {/* quebra linha no mobile */}
                  <span className="sm:hidden">
                    <br />
                  </span>
                  <span className="hidden sm:inline">| </span>
                  Cliente: {getCustomerName(orderDetails.customerId)}
                </span>
              )}
            </p>
          </div>
        )}

        <form onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}>
          <Card className="overflow-hidden border-none shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
              <div className="flex items-center gap-3">
                <ShoppingCart className="h-6 w-6" />
                <CardTitle className="text-xl">
                  {isEditing ? "Editar Pedido" : "Novo Pedido"}
                </CardTitle>
              </div>
            </CardHeader>

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <div className="px-3 sm:px-6 pt-4">
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger
                    value="info"
                    className="data-[state=active]:bg-orange-50 data-[state=active]:text-[#EE7B11] text-xs sm:text-sm"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Informações</span>
                      <span className="sm:hidden">Info</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger
                    value="products"
                    className="data-[state=active]:bg-orange-50 data-[state=active]:text-[#EE7B11] text-xs sm:text-sm"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Produtos</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger
                    value="delivery"
                    className="data-[state=active]:bg-orange-50 data-[state=active]:text-[#EE7B11] text-xs sm:text-sm"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Truck className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Entrega</span>
                    </div>
                  </TabsTrigger>
                </TabsList>
              </div>

              <CardContent className="p-3 sm:p-4 md:p-6 space-y-4">
                <TabsContent value="info" className="mt-0 space-y-4">
                  <FormField
                    control={form.control}
                    name="customerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Cliente
                        </FormLabel>
                        <FormControl>
                          <ComboSearch
                            id="customer"
                            context="customer"
                            value={field.value || ""}
                            onSelect={(value) => {
                              field.onChange(value);
                              if (
                                deliveryTypeWatch === OrderDeliveryType.DELIVERY
                              ) {
                                const selectedCustomerId = value;
                                const selectedCustomer = customers.find(
                                  (customer) =>
                                    customer.id === selectedCustomerId
                                );
                                const addresses =
                                  getCustomerAddresses(selectedCustomer);
                                form.setValue(
                                  "delivery.address",
                                  addresses[0] ?? undefined
                                );
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Tag className="h-4 w-4" />
                          Tags
                        </FormLabel>
                        <FormControl>
                          <MultiTagSelector
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <FormControl>
                            <CustomCombobox
                              value={field.value}
                              onChange={(value) => field.onChange(value)}
                              options={Object.entries(
                                orderStatusTranslation
                              ).map(([value, label]) => ({
                                value,
                                label,
                              }))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="orderDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Data do pedido
                          </FormLabel>
                          <FormControl>
                            <DatePickerComponent
                              id="orderDate"
                              selected={field.value || localDate()}
                              onSelect={(date) =>
                                field.onChange(date || localDate())
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="preparationDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Data de Preparação
                        </FormLabel>
                        <FormControl>
                          <DatePickerComponent
                            id="preparationDate"
                            selected={field.value}
                            onSelect={(date) =>
                              field.onChange(date || localDate())
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Descrição do pedido
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Descrição do pedido"
                            {...field}
                            className="min-h-[100px] resize-y border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="products" className="mt-0 space-y-4">
                  <div className="bg-orange-50 p-4 rounded-md mb-2">
                    <h3 className="font-medium text-[#EE7B11] mb-1 flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      Produtos do Pedido
                    </h3>
                    <p className="text-sm text-gray-600">
                      Adicione os produtos que fazem parte deste pedido.
                    </p>
                  </div>

                  {/* Seleção de Produtos */}
                  <FormField
                    control={form.control}
                    name="products"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <UnifiedProductSelector
                            mode="orders"
                            value={
                              field.value?.map((product) => {
                                const normalizedAdditionals =
                                  product.additionals
                                    ?.filter(
                                      (additional) =>
                                        additional.itemId &&
                                        additional.quantity > 0
                                    )
                                    .map((additional) => ({
                                      itemId: additional.itemId,
                                      quantity: additional.quantity,
                                    })) ?? [];

                                return {
                                  productId: product.productId,
                                  quantity: product.quantity,
                                  additionals:
                                    normalizedAdditionals.length > 0
                                      ? normalizedAdditionals
                                      : undefined,
                                };
                              }) ?? []
                            }
                            onChange={(products) => {
                              field.onChange(
                                products
                                  .filter(
                                    (product) =>
                                      product.productId &&
                                      product.productId.trim() !== ""
                                  )
                                  .map((product) => {
                                    const sanitizedAdditionals =
                                      product.additionals
                                        ?.filter(
                                          (additional) =>
                                            additional.itemId &&
                                            additional.quantity > 0
                                        )
                                        .map((additional) => ({
                                          itemId: additional.itemId,
                                          quantity: additional.quantity,
                                        })) ?? [];

                                    return {
                                      productId: product.productId,
                                      quantity: product.quantity,
                                      additionals:
                                        sanitizedAdditionals.length > 0
                                          ? sanitizedAdditionals
                                          : undefined,
                                    };
                                  })
                              );
                            }}
                            placeholder="Selecione os produtos para este pedido"
                            showBadges={false}
                            showFinancialInfo={true}
                            showMarginInfo={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Análise de Margem - só na tab de produtos */}
                  {watchProducts.length > 0 && (
                    <div className="mt-6">
                      <OrderMarginAnalysis
                        order={{
                          products: watchProducts.map((productRow) => ({
                            productId: productRow.productId,
                            name: getProductName(productRow.productId),
                            quantity: productRow.quantity,
                            unitPrice: getProductPriceValue(
                              productRow.productId
                            ),
                            unitCost: getProductCostValue(productRow.productId),
                            additionals: (() => {
                              const additionals =
                                getProductAdditionalDetails(productRow);
                              return additionals.length > 0
                                ? additionals
                                : undefined;
                            })(),
                          })),
                        }}
                        additional={watchAdditional}
                        discount={discountWatch}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <FormField
                      control={form.control}
                      name="additional"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            Valor adicional
                          </FormLabel>
                          <FormControl>
                            <CurrencyInput
                              {...field}
                              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="discount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            Desconto
                          </FormLabel>
                          <FormControl>
                            <CurrencyInput
                              {...field}
                              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-700">
                        Valor Total:
                      </span>
                      <span className="font-bold text-lg text-[#EE7B11]">
                        {calculateTotalValue()}
                      </span>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="delivery" className="mt-0 space-y-4">
                  <div className="bg-orange-50 p-4 rounded-md mb-2">
                    <h3 className="font-medium text-[#EE7B11] mb-1 flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      Informações de Entrega
                    </h3>
                    <p className="text-sm text-gray-600">
                      Configure como o pedido será entregue ao cliente.
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="delivery.deliveryType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Truck className="h-4 w-4" />
                          Tipo de entrega
                        </FormLabel>
                        <FormControl>
                          <CustomCombobox
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              if (value === OrderDeliveryType.WITHDRAWAL) {
                                form.setValue("delivery.deliveryAt", undefined);
                                form.setValue("delivery.address", undefined);
                                form.setValue("delivery.deliveryValue", 0);
                              }
                            }}
                            options={Object.entries(
                              orderDeliveryTypeTransalation
                            ).map(([value, label]) => ({
                              value,
                              label,
                            }))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {deliveryTypeWatch === OrderDeliveryType.DELIVERY && (
                    <div className="space-y-4 border-l-2 border-orange-200 pl-4 mt-4">
                      <FormField
                        control={form.control}
                        name="delivery.deliveryAt"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              Data e hora de entrega
                            </FormLabel>
                            <FormControl>
                              <DateTimePicker
                                id="deliveryDate"
                                selected={field.value}
                                onSelect={(date) =>
                                  field.onChange(date || localDate())
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="delivery.deliveryValue"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4" />
                              Valor do frete
                            </FormLabel>
                            <FormControl>
                              <CurrencyInput
                                {...field}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="bg-orange-50 p-4 rounded-md">
                        <h4 className="font-medium text-[#EE7B11] mb-2 flex items-center gap-2">
                          Endereço de Entrega
                        </h4>

                        <FormField
                          control={form.control}
                          name="delivery.address"
                          render={({ field }) => {
                            const addresses =
                              getCustomerAddresses(selectedCustomerDetails);

                            return (
                              <FormItem>
                                <FormControl>
                                  <AddressSelectorComponent
                                    addresses={addresses}
                                    value={field.value}
                                    onSelect={(value) => field.onChange(value)}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>
                    </div>
                  )}
                </TabsContent>
              </CardContent>
            </Tabs>

            <CardFooter className="border-t p-3 sm:p-4 md:p-6 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  isEditing && id
                    ? navigate(`/pedidos/${id}`)
                    : navigate("/pedidos")
                }
                className="border-gray-200 text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full sm:w-auto"
              >
                {isEditing ? "Atualizar" : "Salvar"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </Form>
  );
}

